import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Field } from "@/components/Forms";
import { Initials } from "@/components/kit";
import { useStore } from "@/store/AppStore";
import { teamOf } from "@/lib/selectors";
import { brl } from "@/lib/format";
import { api } from "@/lib/api";
import { useAccounts, AccountControl } from "@/components/Accounts";

const COMPANY_FIELDS = [["name", "Nome da empresa"], ["segment", "Segmento"], ["city", "Cidade · UF"], ["owner", "Empresária responsável"], ["since", "Cliente desde"]];
const EMPTY_COMPANY = { name: "", segment: "", city: "", owner: "", since: "Out 2026", salesGoal: "" };

function FormDialog({ title, trigger, initial, fields, numberField, dates = [], onSave, testId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const change = (v) => { if (v) setForm(initial); setOpen(v); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (fields.some(([k]) => !String(form[k] || "").trim())) return toast.error("Preencha todos os campos.");
    if (!(Number(form[numberField[0]]) > 0)) return toast.error("Informe uma meta válida.");
    onSave({ ...form, [numberField[0]]: Number(form[numberField[0]]) });
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="w-[calc(100%-2rem)] rounded-xl" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.map(([k, label]) => <Field key={k} label={label}><Input value={form[k]} onChange={set(k)} data-testid={`${testId}-${k}-input`} /></Field>)}
          <Field label={numberField[1]}><Input type="number" min="0" value={form[numberField[0]]} onChange={set(numberField[0])} data-testid={`${testId}-${numberField[0]}-input`} /></Field>
          {!!dates.length && (
            <div className="grid grid-cols-2 gap-4">
              {dates.map(([k, label]) => <Field key={k} label={label}><Input type="date" value={form[k] || ""} onChange={set(k)} data-testid={`${testId}-${k}-input`} /></Field>)}
            </div>
          )}
          <Button type="submit" className="w-full font-semibold" data-testid={`${testId}-save-button`}>Salvar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CompanyDialog({ company, trigger, testId }) {
  const { addCompany, updateCompany } = useStore();
  const save = (data) => {
    if (company) updateCompany(company.id, data);
    else addCompany(data);
    toast.success(company ? "Empresa atualizada." : "Empresa cadastrada.");
  };
  return <FormDialog title={company ? "Editar empresa" : "Nova empresa"} trigger={trigger} initial={company || EMPTY_COMPANY} fields={COMPANY_FIELDS} numberField={["salesGoal", "Meta de vendas mensal (R$)"]} dates={[["foundedAt", "Fundação da empresa"], ["ownerBirthday", "Aniversário da empresária"]]} onSave={save} testId={testId} />;
}

function CollaboratorDialog({ companyId, person, trigger, testId }) {
  const { addCollaborator, updateCollaborator } = useStore();
  const save = (data) => {
    if (person) updateCollaborator(person.id, data);
    else addCollaborator({ ...data, companyId });
    toast.success(person ? "Colaboradora atualizada." : "Colaboradora adicionada.");
  };
  return <FormDialog title={person ? "Editar colaboradora" : "Nova colaboradora"} trigger={trigger} initial={person || { name: "", role: "", goal: "" }} fields={[["name", "Nome"], ["role", "Função"]]} numberField={["goal", "Meta de vendas mensal (R$)"]} dates={[["birthday", "Aniversário"]]} onSave={save} testId={testId} />;
}

function RemoveButton({ person }) {
  const { removeCollaborator } = useStore();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`remove-collab-${person.id}`} aria-label="Remover"><Trash2 className="h-4 w-4 text-neutral-500" /></Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="w-[calc(100%-2rem)] rounded-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Remover {person.name}?</AlertDialogTitle>
          <AlertDialogDescription>Tarefas, checklists e feedbacks dela serão apagados. As vendas lançadas continuam no total da empresa.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`remove-collab-cancel-${person.id}`}>Cancelar</AlertDialogCancel>
          <AlertDialogAction data-testid={`remove-collab-confirm-${person.id}`} onClick={() => { removeCollaborator(person.id); toast.success("Colaboradora removida."); }}>Remover</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function OwnerAccount({ company }) {
  const { list, refresh } = useAccounts();
  const account = list.find((u) => u.role === "empresaria" && u.companyId === company.id);
  const create = (f) => api.post("/users", { username: f.username, password: f.password, name: company.owner, role: "empresaria", companyId: company.id });
  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid="owner-account">
      <div className="flex items-center gap-3"><Initials name={company.owner} /><div><p className="text-sm font-semibold">{company.owner}</p><p className="text-xs text-neutral-500">{account ? "Acesso ativo" : "Ainda sem acesso ao app"}</p></div></div>
      <AccountControl account={account} create={create} onChange={refresh} testId="owner-account" />
    </div>
  );
}

export function TeamManager({ companyId }) {
  const { state } = useStore();
  const team = teamOf(state, companyId);
  const { list, refresh } = useAccounts();
  const accountOf = (id) => list.find((u) => u.collaboratorId === id);
  const create = (p) => (f) => api.post("/users", { username: f.username, password: f.password, name: p.name, role: "colaboradora", companyId, collaboratorId: p.id });
  return (
    <div data-testid="team-manager">
      <div className="mb-4 flex justify-end">
        <CollaboratorDialog companyId={companyId} testId="new-collab" trigger={<Button className="font-semibold" data-testid="add-collab-button"><UserPlus className="mr-1.5 h-4 w-4" /> Adicionar colaboradora</Button>} />
      </div>
      {!team.length && <p className="py-4 text-sm text-neutral-400">Nenhuma colaboradora cadastrada.</p>}
      <ul className="divide-y divide-neutral-100">
        {team.map((p) => (
          <li key={p.id} data-testid={`team-row-${p.id}`} className="flex items-center gap-4 py-3.5">
            <Initials name={p.name} />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{p.name}</p><p className="text-xs text-neutral-500">{p.role}<span className="sm:hidden"> · Meta {brl(p.goal)}</span></p></div>
            <p className="hidden text-sm text-neutral-600 lg:block">Meta {brl(p.goal)}</p>
            <AccountControl account={accountOf(p.id)} create={create(p)} onChange={refresh} testId={`collab-account-${p.id}`} />
            <CollaboratorDialog person={p} testId={`edit-collab-${p.id}`} trigger={<Button variant="ghost" size="icon" data-testid={`edit-collab-${p.id}-button`} aria-label="Editar"><Pencil className="h-4 w-4 text-gold-dark" /></Button>} />
            <RemoveButton person={p} />
          </li>
        ))}
      </ul>
    </div>
  );
}
